import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Clock, Phone, Package, AlertTriangle, CheckCircle2, FileText, CalendarDays } from "lucide-react";
import { listQuotes, getMaterialsPdfUrl, listAppointments } from "@/lib/api";
import { format, addDays, subDays, parse, isValid, isSameDay, differenceInCalendarDays } from "date-fns";
import VersionPill from "@/components/custom/VersionPill";

const WORKDAY_START = "08:00";
const WORKDAY_END = "17:00";

// Soft navigation limits per spec — prevent accidental drift far outside the relevant window.
const FORWARD_LIMIT_DAYS = 14;
const BACK_LIMIT_DAYS = 30;

function parseDateParam(raw) {
  if (!raw) return null;
  const d = parse(raw, "yyyy-MM-dd", new Date());
  return isValid(d) ? d : null;
}
function startOfDay(d) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }

function getTimeForDay(job, dayDate) {
  if (!job.start_date) return { start: job.booked_start_time || WORKDAY_START, end: job.booked_end_time || WORKDAY_END };
  const sd = parse(job.start_date, "dd/MM/yyyy", new Date());
  const ed = job.end_date ? parse(job.end_date, "dd/MM/yyyy", new Date()) : sd;
  const isMulti = isValid(sd) && isValid(ed) && !isSameDay(sd, ed);
  if (!isMulti) return { start: job.booked_start_time || WORKDAY_START, end: job.booked_end_time || WORKDAY_END };
  if (isSameDay(dayDate, sd)) return { start: job.booked_start_time || WORKDAY_START, end: WORKDAY_END };
  if (isSameDay(dayDate, ed)) return { start: WORKDAY_START, end: job.booked_end_time || WORKDAY_END };
  return { start: WORKDAY_START, end: WORKDAY_END };
}

export default function MobileToday() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Date is owned by the URL so it survives navigation away/back to a job sheet.
  // On mount we read ?date=YYYY-MM-DD; if absent or invalid, default to today and write it back.
  const urlDate = parseDateParam(searchParams.get("date"));
  const [currentDate, setCurrentDate] = useState(urlDate || new Date());
  const [jobs, setJobs] = useState([]);
  const [allQuotes, setAllQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matTab, setMatTab] = useState("not-started");

  // Sync URL ⇆ state. We replace (not push) so day-navigation doesn't pollute the back stack.
  useEffect(() => {
    const expected = format(currentDate, "yyyy-MM-dd");
    if (searchParams.get("date") !== expected) {
      const next = new URLSearchParams(searchParams);
      next.set("date", expected);
      setSearchParams(next, { replace: true });
    }
  }, [currentDate, searchParams, setSearchParams]);

  // If the URL param changes externally (e.g. user pasted a date URL or navigated back from a
  // job sheet whose URL preserves the param), re-sync the in-memory state.
  useEffect(() => {
    const fromUrl = parseDateParam(searchParams.get("date"));
    if (fromUrl && !isSameDay(fromUrl, currentDate)) setCurrentDate(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Soft navigation bounds
  const today0 = startOfDay(new Date());
  const current0 = startOfDay(currentDate);
  const daysFromToday = differenceInCalendarDays(current0, today0);
  const canGoForward = daysFromToday < FORWARD_LIMIT_DAYS;
  const canGoBack = daysFromToday > -BACK_LIMIT_DAYS;

  const memberId = localStorage.getItem("team_member_id");
  const memberName = localStorage.getItem("team_member_name");
  const memberColor = localStorage.getItem("team_member_color");
  const memberInitials = localStorage.getItem("team_member_initials");
  const isLucas = memberInitials === "LH";
  const isAdmin = memberInitials === "LH" || memberInitials === "TC";
  const [showMaterials, setShowMaterials] = useState(false);
  const [dayAppts, setDayAppts] = useState([]);
  const [allSorted, setAllSorted] = useState([]);
  // Monotonic request counter — every fetchDayData call captures its own ID, and on resolve
  // only commits state if the captured ID still matches the latest. This kills the swipe-induced
  // race where Wednesday's slow response could overwrite Thursday's data after the user navigated
  // forwards (the original bug surfaced 26/05/2026 on Kayhan's mobile day view).
  const fetchSeqRef = useRef(0);

  const fetchDayData = useCallback(() => {
    if (!memberId) { navigate("/mobile"); return; }
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    Promise.all([listQuotes(), listAppointments()]).then(([data, appts]) => {
      // Stale-response guard: another fetch started after this one — discard this result.
      if (seq !== fetchSeqRef.current) return;
      setAllQuotes(data);
      const cur = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
      const dayJobs = data.filter(q => {
        if (!q.start_date) return false;
        const isPrimary = q.booked_team_member_id === memberId;
        const isAdditional = (q.additional_team_members || []).some(am => am.team_member_id === memberId);
        if (!isPrimary && !isAdditional) return false;
        try {
          const sd = parse(q.start_date, "dd/MM/yyyy", new Date());
          if (!isValid(sd)) return false;
          const ed = q.end_date ? parse(q.end_date, "dd/MM/yyyy", new Date()) : sd;
          const endValid = isValid(ed) ? ed : sd;
          return cur >= new Date(sd.getFullYear(), sd.getMonth(), sd.getDate()) && cur <= new Date(endValid.getFullYear(), endValid.getMonth(), endValid.getDate());
        } catch { return false; }
      });
      const memberAppts = appts.filter(ap => {
        if (ap.team_member_id !== memberId) return false;
        try {
          const sd = parse(ap.date, "dd/MM/yyyy", new Date());
          if (!isValid(sd)) return false;
          const ed = ap.end_date ? parse(ap.end_date, "dd/MM/yyyy", new Date()) : sd;
          const endValid = isValid(ed) ? ed : sd;
          return cur >= new Date(sd.getFullYear(), sd.getMonth(), sd.getDate()) && cur <= new Date(endValid.getFullYear(), endValid.getMonth(), endValid.getDate());
        } catch { return false; }
      });
      const allEvents = [
        ...dayJobs.map(j => ({ ...j, _eventType: "job" })),
        ...memberAppts.map(a => ({ ...a, _eventType: "appt" })),
      ];
      allEvents.sort((a, b) => {
        const timeA = a._eventType === "job" ? (a.booked_start_time || "08:00") : (a.start_time || "08:00");
        const timeB = b._eventType === "job" ? (b.booked_start_time || "08:00") : (b.start_time || "08:00");
        const cmp = timeA.localeCompare(timeB);
        if (cmp !== 0) return cmp;
        return (a.site_address || a.title || "").toLowerCase().localeCompare((b.site_address || b.title || "").toLowerCase());
      });
      setJobs(allEvents.filter(e => e._eventType === "job"));
      setDayAppts(allEvents.filter(e => e._eventType === "appt"));
      setAllSorted(allEvents);
    }).catch(() => {
      // Errors are silently swallowed today (no toast for the day view). Only flip loading
      // off if we're still the latest fetch — otherwise the latest fetch will manage it.
      if (seq !== fetchSeqRef.current) return;
    }).finally(() => {
      // Only let the latest fetch toggle loading off, so a stale resolve can't flicker the spinner.
      if (seq !== fetchSeqRef.current) return;
      setLoading(false);
    });
  }, [memberId, currentDate, navigate]);

  useEffect(() => { fetchDayData(); }, [fetchDayData]);

  const isToday = isSameDay(currentDate, new Date());

  // Address hiding logic for non-admin team members
  const isJobCompleted = (job) => ["Job Signed", "Awaiting Quote", "Invoiced"].includes(job.status) || (job.booking_type === "recall" && job.status !== "Job Booked");

  const isJobOverdue = (job) => {
    if (isJobCompleted(job)) return false;
    if (job.status !== "Job Booked" && job.status !== "Recall Booked") return false;
    // Only overdue on the actual day of the booking, after 4pm
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const viewing = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    if (viewing.getTime() !== today.getTime()) return false; // Not today's view
    return now.getHours() >= 16;
  };


  const getAddressForJob = (job) => {
    return job.site_address || "No address";
  };

  // Check if last job of the day is incomplete (for reminder)
  const showReminder = (() => {
    if (jobs.length === 0) return false;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const viewing = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    if (viewing.getTime() !== today.getTime()) return false;
    if (now.getHours() < 16) return false;
    const incompleteJobs = jobs.filter(j => !isJobCompleted(j) && (j.status === "Job Booked" || j.status === "Recall Booked"));
    return incompleteJobs.length > 0;
  })();

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      {/* Header */}
      <div className="bg-white border-b px-4 pt-4 pb-3" style={{ borderColor: "var(--border)" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: memberColor || "#64748b" }}>
              {memberInitials}
            </div>
            <span className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>{memberName}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { localStorage.removeItem("team_member_id"); localStorage.removeItem("team_member_name"); localStorage.removeItem("team_member_color"); localStorage.removeItem("team_member_initials"); navigate("/mobile"); }} className="text-xs font-medium" style={{ color: "var(--primary)" }}>
              Switch
            </button>
            {["LH", "TC", "KH"].includes(memberInitials) && (
              <button onClick={() => {
                // Sync mobile session to desktop
                const mobileId = localStorage.getItem("team_member_id");
                const mobileName = localStorage.getItem("team_member_name");
                const mobileInitials = localStorage.getItem("team_member_initials");
                if (mobileId) {
                  localStorage.setItem("desktop_user_id", mobileId);
                  localStorage.setItem("desktop_user_name", mobileName || "");
                  localStorage.setItem("desktop_user_initials", mobileInitials || "");
                }
                navigate("/");
              }} className="text-xs font-medium px-2.5 py-1 rounded-lg" style={{ background: "var(--primary-light)", color: "var(--primary)" }}>
                Dashboard
              </button>
            )}
          </div>
        </div>
        {/* Day navigation */}
        <div className="flex items-center justify-between">
          <button onClick={() => canGoBack && setCurrentDate(subDays(currentDate, 1))} disabled={!canGoBack} className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-testid="mt-prev-day" aria-label="Previous day">
            <ChevronLeft className="w-5 h-5" style={{ color: "var(--text-secondary)" }} />
          </button>
          <div className="text-center">
            <p className="text-lg font-semibold" style={{ color: "var(--text-primary)" }}>{isToday ? "Today" : format(currentDate, "EEEE")}</p>
            <p className="text-xs" style={{ color: "var(--text-secondary)" }}>{format(currentDate, "dd MMMM yyyy")}</p>
            {!isToday && (
              <button onClick={() => setCurrentDate(new Date())} className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "var(--primary-light)", color: "var(--primary)" }} data-testid="mt-jump-today">
                <CalendarDays className="w-3 h-3" /> Jump to today
              </button>
            )}
          </div>
          <button onClick={() => canGoForward && setCurrentDate(addDays(currentDate, 1))} disabled={!canGoForward} className="p-2 -mr-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-testid="mt-next-day" aria-label="Next day">
            <ChevronRight className="w-5 h-5" style={{ color: "var(--text-secondary)" }} />
          </button>
        </div>
      </div>

      {/* Job count */}
      <div className="px-4 py-3 bg-white border-b" style={{ borderColor: "var(--border)" }}>
        <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-secondary)" }}>
          {jobs.length} job{jobs.length !== 1 ? "s" : ""} scheduled
        </p>
      </div>

      {/* Appointments (read-only) */}
      {dayAppts.length > 0 && (
        <div className="px-4 pt-3 space-y-2">
          {dayAppts.map(ap => (
            <div key={ap.id} className="bg-white rounded-xl border-2 border-purple-200 p-4" style={{ background: "#FAF5FF" }}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <span className="badge-pill text-[10px] mb-1 inline-flex" style={{ background: "#F3E8FF", color: "#7C3AED" }}>
                    <CalendarDays className="w-3 h-3 mr-1" /> Appointment
                  </span>
                  <p className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>{ap.title}</p>
                  {ap.notes && <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{ap.notes}</p>}
                </div>
                <div className="flex items-center gap-1 text-xs flex-shrink-0 px-2.5 py-1 rounded-lg" style={{ background: "#F3E8FF", color: "#7C3AED" }}>
                  <Clock className="w-3 h-3" /> {ap.start_time} - {ap.end_time}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Job list */}
      <div className="px-4 py-3 space-y-3">
        {loading ? (
          <p className="text-center py-10 text-sm" style={{ color: "var(--text-muted)" }}>Loading...</p>
        ) : jobs.length === 0 ? (
          <p className="text-center py-10 text-sm" style={{ color: "var(--text-muted)" }}>No jobs scheduled for this day</p>
        ) : (
          jobs.map((job, jobIndex) => {
            return (
            <div key={job.id} className="space-y-0">
              <button
                onClick={() => navigate(`/mobile/job/${job.id}`)}
                className="w-full bg-white rounded-lg text-left transition-all active:scale-[0.98] hover:shadow-md"
                style={{ border: "1px solid #E5E7EB", borderLeft: `4px solid ${memberColor || "var(--primary)"}`, padding: "16px", borderRadius: "8px" }}
                data-testid={`mobile-job-${job.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    {job.status === "Quote Booked" && job.is_overdue && (
                      <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded mb-1 mr-1" style={{ background: "#FFF5F2", color: "#C2410C", border: "1px solid #FED7AA" }} data-testid={`mobile-quote-overdue-${job.id}`}>Quote Overdue</span>
                    )}
                    {(job.status === "Job Signed" || job.status === "Awaiting Quote" || (job.booking_type === "recall" && job.status !== "Job Booked")) && (
                      <span className="badge-pill badge-success mb-1 inline-flex">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        Completed
                      </span>
                    )}
                    {job.booking_type === "quote" && job.status !== "Job Signed" && job.status !== "Awaiting Quote" && (
                      <span className="badge-pill badge-blue mb-1 inline-flex">Quote Appt</span>
                    )}
                    {job.booking_type === "recall" && job.status !== "Job Signed" && job.status !== "Awaiting Quote" && (
                      <span className="badge-pill badge-warning mb-1 inline-flex">Recall</span>
                    )}
                    {job.booking_type === "job" && job.status !== "Job Signed" && job.status !== "Awaiting Quote" && (
                      <span className="badge-pill badge-warning mb-1 inline-flex">Execution</span>
                    )}
                    {job.works_type === "Works Order" && (
                      <span className="mb-1 inline-flex text-[10px] font-bold px-2 py-0.5 rounded-md" style={{ background: "#DBEAFE", color: "#1D4ED8" }}>Works Order</span>
                    )}
                    {isJobOverdue(job) && (
                      <span className="mb-1 inline-flex text-[10px] font-bold px-2 py-0.5 rounded-md" style={{ background: "#FEE2E2", color: "#DC2626" }}>OVERDUE</span>
                    )}
                    <p style={{ fontSize: "14px", fontWeight: 700, color: "var(--text-primary)" }} className="truncate">{getAddressForJob(job)}</p>
                    <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>{job.client_type} · {[job.client_first_name, job.client_last_name].filter(Boolean).join(" ")}</p>
                  </div>
                  {job.booked_start_time && (
                    <div className="flex-shrink-0" style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-primary)" }}>
                      {(() => { const t = getTimeForDay(job, currentDate); return `${t.start} - ${t.end}`; })()}
                    </div>
                  )}
                </div>
                {job.tenant_phone && (
                  <a href={`tel:${job.tenant_phone.replace(/\s/g, "")}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5 mt-3 w-full rounded-md font-semibold text-xs transition-colors active:opacity-80" style={{ background: "#dcfce7", color: "#16a34a", padding: "8px 12px" }}>
                    <Phone className="w-3.5 h-3.5" /> Call Tenant: {job.tenant_phone}
                  </a>
                )}
              </button>
            </div>
            );
          })
        )}
      </div>

      {/* Job pack completion reminder */}
      {showReminder && (
        <div className="mx-4 mt-3 p-3 rounded-xl" style={{ background: "#FEF3C7", border: "2px solid #F59E0B" }}>
          <p className="text-sm font-bold" style={{ color: "#92400E" }}>Reminder</p>
          <p className="text-xs mt-0.5" style={{ color: "#92400E" }}>You have incomplete job pack(s) today. Please complete before finishing your day.</p>
        </div>
      )}

      {/* Lucas: Materials Management */}
      {isLucas && !loading && (
        <div className="px-4 py-3">
          <button
            onClick={() => setShowMaterials(p => !p)}
            className="w-full flex items-center justify-between py-2.5 px-3 rounded-xl mb-3 transition-colors active:scale-[0.99]"
            style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
            data-testid="toggle-materials-btn"
          >
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-secondary)" }}>
              <Package className="w-3.5 h-3.5 inline mr-1.5" />
              Materials
            </span>
            <span className="text-xs font-medium" style={{ color: "var(--primary)" }}>
              {showMaterials ? "Hide" : "Show"}
            </span>
          </button>

          {showMaterials && (
          <>
          {/* Tab switcher */}
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => setMatTab("not-started")}
              className={`flex-1 py-2.5 rounded-xl text-xs font-semibold text-center transition-colors ${matTab === "not-started" ? "text-white" : ""}`}
              style={{ background: matTab === "not-started" ? "var(--danger)" : "var(--bg)", color: matTab === "not-started" ? "#fff" : "var(--text-secondary)", border: `1px solid ${matTab === "not-started" ? "var(--danger)" : "var(--border)"}` }}
            >
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
              Not Started ({allQuotes.filter(q => q.materials_status === "Not Started" && ["Quote Accepted","Job Booked","Job Signed"].includes(q.status)).length})
            </button>
            <button
              onClick={() => setMatTab("ordered")}
              className={`flex-1 py-2.5 rounded-xl text-xs font-semibold text-center transition-colors`}
              style={{ background: matTab === "ordered" ? "var(--success)" : "var(--bg)", color: matTab === "ordered" ? "#fff" : "var(--text-secondary)", border: `1px solid ${matTab === "ordered" ? "var(--success)" : "var(--border)"}` }}
            >
              <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />
              Ordered ({allQuotes.filter(q => q.materials_status === "Ordered").length})
            </button>
          </div>

          {/* Materials list */}
          <div className="space-y-2">
            {(() => {
              const filtered = matTab === "not-started"
                ? allQuotes.filter(q => q.materials_status === "Not Started" && ["Quote Accepted","Job Booked","Job Signed","Invoiced"].includes(q.status))
                : allQuotes.filter(q => q.materials_status === "Ordered");
              if (filtered.length === 0) return <p className="text-center py-6 text-xs" style={{ color: "var(--text-muted)" }}>No jobs</p>;
              return filtered.map(q => (
                <div key={q.id} className="bg-white rounded-xl border p-3" style={{ borderColor: matTab === "not-started" ? "var(--danger)" : "var(--success)", borderWidth: "1.5px" }}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: "var(--text-primary)" }}>{q.site_address || "—"}</p>
                      <p className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>{q.client_type} — {q.status}</p>
                      <p className="text-xs" style={{ color: "var(--text-muted)" }}>{q.item_count || 0} material items</p>
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                      {(q.item_count || 0) > 0 && (
                        <a
                          href={getMaterialsPdfUrl(q.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-colors active:opacity-80"
                          style={{ background: "var(--primary-light)", color: "var(--primary)" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FileText className="w-3 h-3" /> PDF
                        </a>
                      )}
                      <button
                        onClick={() => navigate(`/mobile/job/${q.id}`)}
                        className="flex items-center px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-colors active:opacity-80"
                        style={{ background: "var(--bg)", color: "var(--text-secondary)", border: "1px solid var(--border)" }}
                      >
                        View
                      </button>
                    </div>
                  </div>
                </div>
              ));
            })()}
          </div>
          </>
          )}
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "center", padding: "20px 0 24px" }}>
        <VersionPill />
      </div>
    </div>
  );
}
